import { atom } from "jotai";

export type ComposerService = {
  sendMessage: (message: string, additionalData?: Record<string, any>) => void;
  resetChat: () => void;
};

/**
 * This atom stores a reference to the composer service API.
 * Components can import this atom and use it to send messages to the chat interface.
 */
export const composerServiceAtom = atom<ComposerService | null>(null);

/**
 * Write-only atom that sends a message to the composer.
 * This is convenient for using in other atoms or effects.
 *
 * Example usage:
 * ```
 * const sendMessage = useSetAtom(sendMessageAtom);
 * sendMessage({ message: "Hello", data: { context: "example" } });
 * ```
 *
 * Or in another atom:
 * ```
 * export const myAtom = atom(
 *   null,
 *   (get, set, params) => {
 *     // Do something
 *     set(sendMessageAtom, { message: "Task completed", data: { result: params } });
 *   }
 * );
 * ```
 */
export const sendMessageAtom = atom(
  null,
  (get, set, params: { message: string; data?: Record<string, any> }) => {
    const composerService = get(composerServiceAtom);
    if (composerService) {
      composerService.sendMessage(params.message, params.data);
    } else {
      console.error(
        "Attempted to send message, but composer service is not available:",
        params,
      );
    }
  },
);

/**
 * Write-only atom that resets the composer chat.
 */
export const resetChatAtom = atom(null, (get) => {
  const composerService = get(composerServiceAtom);
  if (composerService) {
    composerService.resetChat();
  } else {
    console.error(
      "Attempted to reset chat, but composer service is not available",
    );
  }
});

composerServiceAtom.debugLabel = "composerServiceAtom";
sendMessageAtom.debugLabel = "sendMessageAtom";
resetChatAtom.debugLabel = "resetChatAtom";
